/**
 * Client-side image compression — resize + JPEG re-encode before upload.
 *
 * Why we bother:
 *   Users on mobile in mainland China take photos that are 3-8 MB straight
 *   from the phone camera. Uploading the raw file requires 10+ chunk RTTs
 *   through the K8s ingress → backend → Cloudflare R2 path, which is very
 *   slow when the client sits behind the Great Firewall (typical 200-500 ms
 *   RTT to CF edge). By resizing to a max 2000 px long edge and JPEG 0.85
 *   BEFORE chunking, we cut the payload ~5-10× and the number of chunks
 *   shrinks accordingly.
 *
 * Preserves EXIF orientation implicitly by drawing through ImageBitmap
 * (browsers apply the rotation on decode). PNG / GIF are left untouched
 * because re-encoding a screenshot or logo to JPEG would ruin quality.
 *
 * @param {File} file
 * @param {{ maxEdge?: number, quality?: number, minBytes?: number }} opts
 * @returns {Promise<File>} same File if compression would not help; else a
 *   new File named ``${originalName}.jpg``.
 */
export async function maybeCompressImage(file, opts = {}) {
  const { maxEdge = 2000, quality = 0.85, minBytes = 300 * 1024, targetBytes = 3 * 1024 * 1024 } = opts;
  // Only touch JPEG / HEIC-decoded / WebP. PNG stays untouched — screenshots
  // and logos are almost always PNG and shouldn't be JPEG-encoded.
  // 2026-08 · HEIC support: browsers can't natively decode .heic/.heif via
  // createImageBitmap outside Safari. When we detect one, keep the raw file
  // and let the SERVER convert it (backend has libheif via pillow-heif).
  if (!file || !file.type) return file;
  if (/^image\/(heic|heif)$/i.test(file.type) || /\.hei[cf]$/i.test(file.name || "")) {
    // No client compression — server will decode + reencode to JPEG.
    return file;
  }
  if (!/^image\/(jpeg|jpg|webp|png)$/i.test(file.type)) {
    return file;
  }
  // Nothing to gain for already-small photos — skip the decode cost.
  if (file.size < minBytes) return file;
  try {
    const bitmap = await createImageBitmap(file);
    let currentQ = quality;
    let scale = Math.min(1, maxEdge / Math.max(bitmap.width, bitmap.height));
    let out = null;
    // Try up to 4 quality steps to land under ``targetBytes`` (3 MB by default).
    for (let attempt = 0; attempt < 4; attempt++) {
      const w = Math.round(bitmap.width * scale);
      const h = Math.round(bitmap.height * scale);
      const canvas = typeof OffscreenCanvas !== "undefined"
        ? new OffscreenCanvas(w, h)
        : Object.assign(document.createElement("canvas"), { width: w, height: h });
      const ctx = canvas.getContext("2d");
      if (!ctx) return file;
      ctx.drawImage(bitmap, 0, 0, w, h);
      const blob = canvas.convertToBlob
        ? await canvas.convertToBlob({ type: "image/jpeg", quality: currentQ })
        : await new Promise((res) => canvas.toBlob(res, "image/jpeg", currentQ));
      if (!blob) break;
      out = blob;
      if (blob.size <= targetBytes) break;
      // Still too big — reduce quality and/or scale, then try again.
      if (currentQ > 0.6) currentQ -= 0.15;
      else scale *= 0.8;
    }
    if (bitmap.close) bitmap.close();
    if (!out || out.size >= file.size) return file; // never grow the payload
    const stem = (file.name || "photo").replace(/\.[^./]+$/, "");
    return new File([out], `${stem}.jpg`, { type: "image/jpeg", lastModified: Date.now() });
  } catch {
    return file;
  }
}
